/**
 * ABM de aplicaciones integradas al SSO.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '@presentation/components/common';
import { RequireRole } from '@presentation/router/RequireRole';
import { useApplications } from '@presentation/hooks';
import { SYSTEM_ROLES } from '@shared/constants';
import type { Application } from '@domain/entities';
import styles from '../admin.module.css';

const emptyForm = {
  code: '',
  name: '',
  description: '',
  cognitoClientId: '',
  redirectUris: '',
  logoutUris: '',
};

export function ApplicationsPage() {
  const { applications, isLoading, error, save, toggleStatus } = useApplications();
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState<Application | null>(null);

  const startEdit = (application: Application) => {
    setEditing(application);
    setForm({
      code: application.code,
      name: application.name,
      description: application.description ?? '',
      cognitoClientId: application.cognitoClientId ?? '',
      redirectUris: application.redirectUris.join('\n'),
      logoutUris: application.logoutUris.join('\n'),
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();

    const saved = await save(
      {
        code: form.code,
        name: form.name,
        description: form.description || undefined,
        cognitoClientId: form.cognitoClientId || undefined,
        redirectUris: splitUris(form.redirectUris),
        logoutUris: splitUris(form.logoutUris),
      },
      editing?.id
    );

    if (saved) cancelEdit();
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Aplicaciones</h1>
          <p className={styles.subtitle}>
            Sistemas integrados al login unificado. Cada uno tiene su app client en el proveedor
            de identidad.
          </p>
        </div>
      </header>

      {error && <p className={styles.error}>{error}</p>}

      <RequireRole roles={[SYSTEM_ROLES.admin]}>
        <form className={styles.form} onSubmit={submit}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="code">
              Codigo
            </label>
            <input
              id="code"
              className={styles.input}
              value={form.code}
              onChange={event => setForm({ ...form, code: event.target.value })}
              placeholder="sc-manager"
              disabled={!!editing}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="name">
              Nombre
            </label>
            <input
              id="name"
              className={styles.input}
              value={form.name}
              onChange={event => setForm({ ...form, name: event.target.value })}
              required
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="cognitoClientId">
              App client
            </label>
            <input
              id="cognitoClientId"
              className={styles.input}
              value={form.cognitoClientId}
              onChange={event => setForm({ ...form, cognitoClientId: event.target.value })}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="redirectUris">
              Callbacks (una por linea)
            </label>
            <textarea
              id="redirectUris"
              className={styles.input}
              rows={2}
              value={form.redirectUris}
              onChange={event => setForm({ ...form, redirectUris: event.target.value })}
              placeholder="https://manager.empresa.com/auth/callback"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="logoutUris">
              Logout (una por linea)
            </label>
            <textarea
              id="logoutUris"
              className={styles.input}
              rows={2}
              value={form.logoutUris}
              onChange={event => setForm({ ...form, logoutUris: event.target.value })}
            />
          </div>

          <div className={styles.actions}>
            <Button type="submit">{editing ? 'Guardar cambios' : 'Registrar'}</Button>
            {editing && (
              <Button type="button" variant="secondary" onClick={cancelEdit}>
                Cancelar
              </Button>
            )}
          </div>
        </form>
      </RequireRole>

      {isLoading ? (
        <p className={styles.empty}>Cargando aplicaciones...</p>
      ) : applications.length === 0 ? (
        <p className={styles.empty}>Todavia no hay aplicaciones integradas.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Codigo</th>
              <th>Nombre</th>
              <th>App client</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {applications.map(application => (
              <tr key={application.id}>
                <td>{application.code}</td>
                <td>{application.name}</td>
                <td>{application.cognitoClientId ?? '—'}</td>
                <td>
                  <span
                    className={`${styles.badge} ${
                      application.status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive
                    }`}
                  >
                    {application.status === 'ACTIVE' ? 'Activa' : 'Inactiva'}
                  </span>
                </td>
                <td>
                  <RequireRole roles={[SYSTEM_ROLES.admin]}>
                    <div className={styles.actions}>
                      <Button size="small" variant="secondary" onClick={() => startEdit(application)}>
                        Editar
                      </Button>
                      <Button
                        size="small"
                        variant={application.status === 'ACTIVE' ? 'danger' : 'primary'}
                        onClick={() => void toggleStatus(application)}
                      >
                        {application.status === 'ACTIVE' ? 'Dar de baja' : 'Reactivar'}
                      </Button>
                    </div>
                  </RequireRole>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function splitUris(value: string): string[] {
  return value
    .split('\n')
    .map(uri => uri.trim())
    .filter(Boolean);
}
